import type { HostCommandData } from '@sb/feathers-types';
import * as log from '@sb/log';

import { getService } from '../utils';

import {
  clearServicesRestarting,
  markServicesRestarting,
} from './servicesRestartTracker';

const SERVICE_NAME = 'hostCommand';

export async function executeHostCommand(
  data: HostCommandData,
  outputCallback?: (output: string) => void,
  errorCallback?: (error: Error) => void,
): Promise<boolean> {
  const hostCommandService = getService(SERVICE_NAME)();
  hostCommandService.timeout = 300000;

  let stdoutHandler:
    | ((event: {
        identifier?: string;
        command: string;
        output: string;
      }) => void)
    | undefined;

  if (outputCallback) {
    // Filter stdout events to only this command
    stdoutHandler = (event: {
      identifier?: string;
      command: string;
      output: string;
    }) => {
      if (event.command === data.command) {
        outputCallback(event.output);
      }
    };

    hostCommandService.on('stdout', stdoutHandler);
  }

  // Restarting botman (directly or via 'all' — the schema's default when
  // no service is named) severs the agent's tool backend; record it so
  // the chat composer can gate sends until the connection recovers.
  const restartsAgentBackend =
    data.command === 'services_restart' &&
    ['botman', 'all'].includes(data.service ?? 'all');

  if (restartsAgentBackend) {
    markServicesRestarting();
  }

  try {
    await hostCommandService.create(data);

    return true;
  } catch (error) {
    log.error('Host Command Service', 'Error on host command service', {
      error,
    });

    // The restart never started, so there is no connection drop for the
    // gate to wait on; release it rather than holding the composer until
    // the no-disconnect timeout.
    if (restartsAgentBackend) {
      clearServicesRestarting();
    }

    if (errorCallback) {
      try {
        errorCallback(error as Error);
      } catch (callbackError) {
        log.error('Host Command Service', 'Error in errorCallback', {
          error: callbackError,
        });
      }
    }

    return false;
  } finally {
    if (stdoutHandler) {
      hostCommandService.removeListener('stdout', stdoutHandler);
    }
  }
}
